/**
 * API Repository – daftar endpoint dan lapisan fetch ke server.
 * Semua komunikasi HTTP lewat sini; service layer memanggil repository.
 */

// Di app terpasang webOS, origin = file:// → fetch('/x') jadi file:///x (tidak didukung).
// Pakai path relatif (tanpa leading slash) agar resolve ke folder app.
const isFileOrigin =
  typeof location !== 'undefined' && String(location?.protocol || '') === 'file:';

/** Runtime check: hanya origin http(s) yang boleh fetch URL relatif (file:/// dll. tidak didukung Fetch API). */
function isHttpOrigin() {
  try {
    const p = String(typeof location !== 'undefined' ? location?.protocol : '');
    return p === 'http:' || p === 'https:';
  } catch {
    return false;
  }
}

/** URL absolut (http/https) aman untuk fetch dari origin mana pun. Relatif → di file:// jadi file:///... (error). */
function isAbsoluteHttpUrl(url) {
  if (typeof url !== 'string' || !url.trim()) return false;
  const u = url.trim();
  return u.startsWith('http://') || u.startsWith('https://');
}

// ---------------------------------------------------------------------------
// Daftar endpoint (satu sumber kebenaran)
// ---------------------------------------------------------------------------

export const ENDPOINTS = {
  // Base URL for API 001 (/webos/*). If empty, uses relative paths on same origin.
  // __USE_RELATIVE_WEBOS__ true (build --mode preview) → same-origin /webos for proxy; no CORS.
  WEBOS_BASE:
    globalThis.__USE_RELATIVE_WEBOS__ === true
      ? ''
      : (globalThis.__BASE_URL__ || globalThis.__WEBOS_API_BASE__ || ''),
  // Control (remote + fallback lokal). Local hanya dicoba bila origin http(s).
  CONTROL: {
    primary: globalThis.__CONTROL_URL__ || '',
    local: ['/control.json', 'control.json'],
  },
  // Legacy playlist (GET) – kept for backward compatibility
  PLAYLIST: 'https://dev.didapor.id/api/playlist',
  // App info & update (GET). Hanya di-fetch bila origin http(s); di file:// pakai default/skip.
  APP_INFO: '/appinfo.json',
  UPDATE_MANIFEST: '/update.json',
};

// ---------------------------------------------------------------------------
// Fetch helpers (fetch only, no extra deps)
// ---------------------------------------------------------------------------

const DEFAULT_TIMEOUT_MS = 8000;

function isNetworkError(e) {
  const msg = String(e?.message || e);
  return (
    e?.name === 'TypeError' ||
    /failed to fetch|network error|load failed|networkrequestfailed/i.test(msg)
  );
}

/**
 * XHR fallback for environments where fetch fails (e.g. webOS Signage browser).
 * Returns a Response-like object: { ok, status, text(), json() }.
 */
function xhrRequest(url, { method = 'GET', body, headers = {}, timeout = DEFAULT_TIMEOUT_MS }) {
  return new Promise((resolve, reject) => {
    const x = new XMLHttpRequest();
    const t = setTimeout(() => {
      x.abort();
      reject(new Error('Request timeout'));
    }, timeout);
    x.open(method, url, true);
    x.setRequestHeader('Accept', 'application/json');
    if (headers.Authorization) x.setRequestHeader('Authorization', headers.Authorization);
    if (body != null && method !== 'GET') {
      x.setRequestHeader('Content-Type', 'application/json');
    }
    x.onload = () => {
      clearTimeout(t);
      resolve({
        ok: x.status >= 200 && x.status < 300,
        status: x.status,
        text: () => Promise.resolve(x.responseText),
        json: () =>
          Promise.resolve(
            (() => {
              try {
                return JSON.parse(x.responseText || 'null');
              } catch {
                return null;
              }
            })()
          ),
      });
    };
    x.onerror = () => {
      clearTimeout(t);
      reject(new Error('Failed to fetch'));
    };
    x.ontimeout = () => {
      clearTimeout(t);
      reject(new Error('Request timeout'));
    };
    x.timeout = timeout;
    x.send(method !== 'GET' && body != null ? (typeof body === 'string' ? body : JSON.stringify(body)) : null);
  });
}

function webosUrl(path) {
  const p = path.startsWith('/') ? path : `/${path}`;
  const base = String(ENDPOINTS.WEBOS_BASE || '').trim();
  if (!base) return p;
  const b = base.endsWith('/') ? base.slice(0, -1) : base;
  return `${b}${p}`;
}

function withAuthHeaders(headers = {}, token) {
  if (!token) return headers;
  return { ...headers, Authorization: `Bearer ${token}` };
}

async function jsonSafe(res) {
  try {
    return await res.json();
  } catch {
    return null;
  }
}

/**
 * GET request dengan timeout opsional.
 * Fallback ke XMLHttpRequest jika fetch gagal (e.g. webOS Signage browser).
 * @param {string} url
 * @param {{ timeout?: number, signal?: AbortSignal, headers?: Record<string,string> }} options
 * @returns {Promise<Response|{ok,status,text(),json()}>}
 */
export async function get(url, options = {}) {
  if (!isHttpOrigin() && !isAbsoluteHttpUrl(url)) {
    return Promise.reject(new Error('Fetch to local file not supported'));
  }
  const { timeout = DEFAULT_TIMEOUT_MS, signal: externalSignal, headers = {} } = options;
  const ctrl = new AbortController();
  const signal = externalSignal || ctrl.signal;
  if (!externalSignal && timeout > 0) {
    setTimeout(() => ctrl.abort(), timeout);
  }
  try {
    return await fetch(url, { method: 'GET', cache: 'no-store', signal, headers });
  } catch (e) {
    if (isNetworkError(e)) {
      return xhrRequest(url, { method: 'GET', headers, timeout });
    }
    throw e;
  }
}

/**
 * POST JSON dengan timeout opsional.
 * Fallback ke XMLHttpRequest jika fetch gagal (e.g. webOS Signage browser).
 * @param {string} url
 * @param {object} body
 * @param {{ timeout?: number, signal?: AbortSignal, headers?: Record<string,string> }} options
 * @returns {Promise<Response|{ok,status,text(),json()}>}
 */
export async function post(url, body, options = {}) {
  if (!isHttpOrigin() && !isAbsoluteHttpUrl(url)) {
    return Promise.reject(new Error('Fetch to local file not supported'));
  }
  const { timeout = DEFAULT_TIMEOUT_MS, signal: externalSignal, headers = {} } = options;
  const ctrl = new AbortController();
  const signal = externalSignal || ctrl.signal;
  if (!externalSignal && timeout > 0) {
    setTimeout(() => ctrl.abort(), timeout);
  }
  try {
    return await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...headers },
      body: JSON.stringify(body),
      keepalive: false,
      cache: 'no-store',
      signal,
    });
  } catch (e) {
    if (isNetworkError(e)) {
      return xhrRequest(url, {
        method: 'POST',
        body,
        headers: { 'Content-Type': 'application/json', ...headers },
        timeout,
      });
    }
    throw e;
  }
}

/**
 * GET with Bearer token (API 001).
 * @param {string} url
 * @param {{ token?: string, timeout?: number, signal?: AbortSignal }} options
 */
export function getWithAuth(url, options = {}) {
  const { token, ...rest } = options;
  return get(url, { ...rest, headers: withAuthHeaders(rest.headers || {}, token) });
}

/**
 * POST JSON with Bearer token (API 001).
 * @param {string} url
 * @param {object} body
 * @param {{ token?: string, timeout?: number, signal?: AbortSignal }} options
 */
export function postWithAuth(url, body, options = {}) {
  const { token, ...rest } = options;
  return post(url, body, { ...rest, headers: withAuthHeaders(rest.headers || {}, token) });
}

/**
 * Request helper returning { ok, status, json } (API 001).
 * @param {() => Promise<Response>} fn
 */
async function requestJson(fn) {
  const res = await fn();
  const json = await jsonSafe(res);
  return { ok: res.ok, status: res.status, json };
}

// ---------------------------------------------------------------------------
// Repository methods – satu endpoint / kebutuhan = satu fungsi
// ---------------------------------------------------------------------------

/**
 * Control: coba urls berurutan, kembalikan JSON pertama yang ok.
 * Di origin file:// (app terpasang webOS) hindari fetch ke file lokal (Fetch API tidak dukung file://).
 * @returns {Promise<object|null>}
 */
export async function fetchControlRaw() {
  const primary = ENDPOINTS.CONTROL.primary;
  const localUrls = isHttpOrigin() ? (ENDPOINTS.CONTROL.local || []) : [];
  const urls = [primary, ...localUrls].filter(Boolean);
  for (const u of urls) {
    try {
      const sep = u.includes('?') ? '&' : '?';
      const res = await get(`${u}${sep}t=${Date.now()}`);
      if (res.ok) return await res.json();
    } catch {}
  }
  return null;
}

/**
 * Device report: POST payload ke url.
 * @param {string} url
 * @param {object} payload
 * @returns {Promise<{ ok: boolean, error?: string }>}
 */
export async function postDeviceReport(url, payload) {
  try {
    const res = await post(url, payload);
    return { ok: res.ok };
  } catch (e) {
    return { ok: false, error: String(e?.message || e) };
  }
}

/**
 * Playlist: GET JSON dari url (timeout 8s).
 * @param {string} [url] – default ENDPOINTS.PLAYLIST
 * @param {{ timeout?: number }} [options]
 * @returns {Promise<object>}
 */
export async function fetchPlaylistRaw(url, options = {}) {
  const target = url || ENDPOINTS.PLAYLIST;
  const res = await get(target, { timeout: options.timeout ?? DEFAULT_TIMEOUT_MS });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

/**
 * App info: GET /appinfo.json.
 * Di origin file:// tidak pakai fetch (file:// tidak didukung); kembalikan default.
 * @returns {Promise<{ id: string, version: string, raw?: object }>}
 */
export async function fetchAppInfo() {
  if (!isHttpOrigin()) {
    return {
      id: globalThis.__APPINFO_ID__ || 'com.lg.app.signage.dev',
      version: globalThis.__APPINFO_VERSION__ || '1.0.0',
      raw: {},
    };
  }
  const res = await get(ENDPOINTS.APP_INFO);
  if (!res.ok) throw new Error('appinfo fetch failed');
  const json = await res.json();
  return {
    id: json?.id || '',
    version: json?.version || '0.0.0',
    raw: json,
  };
}

/**
 * Update manifest: coba extraUrls lalu default, kembalikan objek { version, url?/ipkUrl? } atau null.
 * Di origin file:// jangan fetch update.json lokal (file:// tidak didukung).
 * @param {string[]} [extraUrls]
 * @returns {Promise<{ version: string, url?: string, ipkUrl?: string }|null>}
 */
export async function fetchUpdateManifestRaw(extraUrls = []) {
  const defaultUrl = ENDPOINTS.UPDATE_MANIFEST;
  const addDefault =
    isHttpOrigin() || (defaultUrl && defaultUrl.startsWith('http'));
  const urls = [...extraUrls, ...(addDefault && defaultUrl ? [defaultUrl] : [])].filter(Boolean);
  for (const u of urls) {
    try {
      const res = await get(u);
      if (res.ok) {
        const m = await res.json();
        if (m?.version && (m?.url || m?.ipkUrl)) return m;
      }
    } catch {}
  }
  return null;
}

// ---------------------------------------------------------------------------
// API 001 (/webos/*) – repository methods (no mapping)
// ---------------------------------------------------------------------------

export const WEBOS_ENDPOINTS = {
  pair: () => webosUrl('/webos/pair'),
  current: () => webosUrl('/webos/current'),
  playlist: () => webosUrl('/webos/playlist'),
  heartbeat: () => webosUrl('/webos/heartbeat'),
  proofOfPlay: () => webosUrl('/webos/proof-of-play'),
  commands: () => webosUrl('/webos/commands'),
};

export async function postWebosPair(body, options = {}) {
  return requestJson(() => post(WEBOS_ENDPOINTS.pair(), body, options));
}

export async function getWebosCurrent(token, options = {}) {
  return requestJson(() => getWithAuth(WEBOS_ENDPOINTS.current(), { ...options, token }));
}

export async function getWebosPlaylist(token, options = {}) {
  return requestJson(() => getWithAuth(WEBOS_ENDPOINTS.playlist(), { ...options, token }));
}

export async function postWebosHeartbeat(token, options = {}) {
  // Spec doesn’t define body; send empty object.
  return requestJson(() => postWithAuth(WEBOS_ENDPOINTS.heartbeat(), {}, { ...options, token }));
}

export async function postWebosProofOfPlay(token, body, options = {}) {
  return requestJson(() =>
    postWithAuth(WEBOS_ENDPOINTS.proofOfPlay(), body, { ...options, token })
  );
}

export async function getWebosCommands(token, options = {}) {
  return requestJson(() => getWithAuth(WEBOS_ENDPOINTS.commands(), { ...options, token }));
}
